import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getOrganisationBlockedStatus, organisationBlockedSignInPath } from '../lib/authErrors';
import { ACTIVE_ROLE_STORAGE_KEY, roleToApiRole } from '../lib/roles';
import { signOutUser } from './auth';

function isInactiveAccountError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) {
    return false;
  }
  const data = error.response.data;
  if (typeof data === 'string') {
    return /inactive|deactivated/i.test(data);
  }
  if (data && typeof data === 'object') {
    const payload = data as { error?: string; message?: string | string[] };
    const parts = [payload.error, payload.message].flat().filter(Boolean).map(String);
    return parts.some((part) => /inactive|deactivated/i.test(part));
  }
  return false;
}

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? session.tokens?.accessToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeRole = roleToApiRole(window.localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY) as any);
    if (activeRole) {
      config.headers['X-Active-Role'] = activeRole;
    }
  } catch {
    // No active session — request goes out unauthenticated
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (isInactiveAccountError(error)) {
      try {
        await signOutUser();
      } catch {
        // ignore sign-out errors
      }
      window.location.replace('/signin?reason=deactivated');
      return Promise.reject(error);
    }

    const orgBlockedStatus = getOrganisationBlockedStatus(error);
    if (orgBlockedStatus) {
      try {
        await signOutUser();
      } catch {
        // ignore sign-out errors
      }
      window.location.replace(organisationBlockedSignInPath(orgBlockedStatus));
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.idToken?.toString() ?? session.tokens?.accessToken?.toString();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch {
        window.location.replace('/signin');
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (data: { email: string; password: string }) => api.post('/auth/signup', data),
  recordLogin: () => api.post('/auth/record-login'),
  me: () => api.get('/auth/me'),
  updateProfile: (data: any) => api.put('/auth/profile', data),
  changePassword: (currentPassword: string, newPassword: string) => api.put('/auth/change-password', { currentPassword, newPassword }),
};

export const orgAPI = {
  getAll: (params?: { status?: string; plan?: string; search?: string; page?: number; pageSize?: number }) =>
    api.get('/organisations', { params: params ?? {} }),
  getById: (id: string | number) => api.get(`/organisations/${id}`),
  create: (data: any) => api.post('/organisations', data),
  update: (id: string | number, data: any) => api.put(`/organisations/${id}`, data),
  patchContent: (
    id: string | number,
    data: {
      departmentOptions?: string[];
      employeeHelpMarkdown?: string;
      logoUrl?: string | null;
      inviteEmailTemplate?: Record<string, unknown> | null;
      helpModel?: 'engage' | 'freeman' | 'both';
    },
  ) => api.patch(`/organisations/${id}/content`, data),
  uploadLogo: (id: string | number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/organisations/${id}/logo/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const campaignAPI = {
  getAll: (params?: { organisationId?: string; page?: number; pageSize?: number }) =>
    api.get('/campaigns', { params: params ?? {} }),
  getById: (id: string | number) => api.get(`/campaigns/${id}`),
  create: (data: any) => api.post('/campaigns', data),
  update: (id: string | number, data: any) => api.put(`/campaigns/${id}`, data),
};

export const notificationJobAPI = {
  get: (id: string) => api.get(`/notification-jobs/${id}`),
  listActive: (organisationId: string) =>
    api.get('/notification-jobs/active', { params: { organisationId } }),
};

export const employeeAPI = {
  getAll: (params?: {
    organisationId?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    department?: string;
    assessmentStatus?: string;
    role?: 'employee' | 'org_manager';
    managerId?: string;
  }) => api.get('/employees', { params: params ?? {} }),
  invite: (emails: string[], campaignId?: string) => api.post('/employees/invite', { emails, campaignId }),
  remind: (id: string) => api.post(`/employees/${id}/remind`),
  reinvite: (id: string) => api.post(`/employees/${id}/reinvite`),
  bulkRemind: (employeeIds: string[]) => api.post('/employees/bulk-remind', { employeeIds }),
  getManagers: () => api.get('/employees/managers'),
  getManagerAnalyticsReadiness: () => api.get('/employees/manager-analytics-readiness'),
  getManagerForEmployee: (id: string) => api.get(`/employees/${id}/manager`),
  assignManager: (id: string, managerId: string | null) => api.put(`/employees/${id}/manager`, { managerId }),
  updateOrgRole: (id: string, role: 'employee' | 'org_manager') => api.put(`/employees/${id}/role`, { role }),
  updateEmail: (id: string, email: string) => api.put(`/employees/${id}/email`, { email }),
  bulkUpload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/employees/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkRepairHierarchy: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/employees/bulk-repair-hierarchy', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

const EMPLOYEE_LIST_PAGE_SIZE = 200;

/** Fetch every active employee row for an org (server caps page size at 200). */
export async function fetchAllEmployees(params?: {
  organisationId?: string;
  search?: string;
  department?: string;
  assessmentStatus?: string;
  role?: 'employee' | 'org_manager';
  managerId?: string;
}): Promise<{ items: Record<string, unknown>[]; total: number }> {
  const items: Record<string, unknown>[] = [];
  let page = 1;
  let total = 0;

  while (true) {
    const response = await employeeAPI.getAll({ ...params, page, pageSize: EMPLOYEE_LIST_PAGE_SIZE });
    const payload = response.data ?? {};
    const batch = Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload)
        ? payload
        : [];
    total = Number(payload.total ?? items.length + batch.length);
    items.push(...(batch as Record<string, unknown>[]));
    if (items.length >= total || batch.length < EMPLOYEE_LIST_PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return { items, total };
}

function buildPlatformAnalyticsParams(opts: {
  from?: string;
  to?: string;
  modelName?: string;
  industry?: string;
}): Record<string, string> {
  const params: Record<string, string> = {};
  if (opts.from) params.from = opts.from;
  if (opts.to) params.to = opts.to;
  if (opts.modelName) params.modelName = opts.modelName;
  if (opts.industry && opts.industry !== 'all') params.industry = opts.industry;
  return params;
}

export const assessmentAPI = {
  getModels: () => api.get('/assessments/models'),
  createModel: (data: any) => api.post('/assessments/models', data),
  updateModel: (id: string | number, data: any) => api.put(`/assessments/models/${id}`, data),
  getModelQuestions: (modelId: string | number) => api.get(`/assessments/models/${modelId}/questions`),
  getActiveCampaign: (campaignId?: string) =>
    api.get('/assessments/active-campaign', { params: campaignId ? { campaignId } : {} }),
  getActiveCampaigns: () => api.get('/assessments/active-campaigns'),
  getCampaignEnrolContext: (campaignId: string) =>
    api.get('/assessments/campaign-enrol-context', { params: { campaignId } }),
  assignCampaign: (campaignId: string) => api.post('/assessments/assign-campaign', { campaignId }),
  submit: (campaignId: string | number, answers: Record<string, unknown>, followUpResponses?: string[]) =>
    api.post('/assessments/submit', { campaignId, answers, followUpResponses }),
  getMyResults: () => api.get('/assessments/my-results'),
  ensureAiSummary: (responseId: string) =>
    api.post(`/assessments/my-results/${responseId}/ai-summary`),
  ensureBorderlineMessage: (responseId: string) =>
    api.post(`/assessments/my-results/${responseId}/borderline-message`),
  downloadMyReportPdf: (responseId?: string) =>
    api.get('/assessments/my-report-pdf', {
      params: responseId ? { responseId } : {},
      responseType: 'blob',
    }),
  getMyActionPlan: () => api.get('/assessments/my-action-plan'),
  toggleAction: (id: string | number) => api.put(`/assessments/action-plan/${id}/toggle`),
  getOrgResults: (organisationId?: string, campaignId?: string, modelId?: string, hierarchyScope?: 'entire_tree' | 'direct_only') => {
    const params: Record<string, string> = {};
    if (organisationId) params.organisationId = organisationId;
    if (campaignId && campaignId !== 'all') params.campaignId = campaignId;
    if (modelId && modelId !== 'all') params.modelId = modelId;
    if (hierarchyScope) params.hierarchyScope = hierarchyScope;
    return api.get('/assessments/org-results', { params });
  },
  getOrgDashboard: (
    organisationId?: string,
    campaignId?: string,
    from?: string,
    to?: string,
    modelId?: string,
    hierarchyScope?: 'entire_tree' | 'direct_only',
  ) => {
    const params: Record<string, string> = {};
    if (organisationId) params.organisationId = organisationId;
    if (campaignId && campaignId !== 'all') params.campaignId = campaignId;
    if (modelId && modelId !== 'all') params.modelId = modelId;
    if (from) params.from = from;
    if (to) params.to = to;
    if (hierarchyScope) params.hierarchyScope = hierarchyScope;
    return api.get('/assessments/org-dashboard', { params });
  },
  getCampaignResponses: (input: {
    organisationId?: string;
    campaignId: string;
    hierarchyScope?: 'entire_tree' | 'direct_only';
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
    state?: string;
    department?: string;
    q?: string;
  }) => {
    const params: Record<string, string> = {
      campaignId: input.campaignId,
    };
    if (input.organisationId) params.organisationId = input.organisationId;
    if (input.hierarchyScope) params.hierarchyScope = input.hierarchyScope;
    if (input.page) params.page = String(input.page);
    if (input.pageSize) params.pageSize = String(input.pageSize);
    if (input.from) params.from = input.from;
    if (input.to) params.to = input.to;
    if (input.state && input.state !== 'all') params.state = input.state;
    if (input.department && input.department !== 'all') params.department = input.department;
    if (input.q?.trim()) params.q = input.q.trim();
    return api.get('/assessments/campaign-responses', { params });
  },
  downloadCampaignResponsesCsv: (input: {
    organisationId?: string;
    campaignId: string;
    hierarchyScope?: 'entire_tree' | 'direct_only';
    from?: string;
    to?: string;
    state?: string;
    department?: string;
    q?: string;
  }) => {
    const params: Record<string, string> = {
      campaignId: input.campaignId,
    };
    if (input.organisationId) params.organisationId = input.organisationId;
    if (input.hierarchyScope) params.hierarchyScope = input.hierarchyScope;
    if (input.from) params.from = input.from;
    if (input.to) params.to = input.to;
    if (input.state && input.state !== 'all') params.state = input.state;
    if (input.department && input.department !== 'all') params.department = input.department;
    if (input.q?.trim()) params.q = input.q.trim();
    return api.get('/assessments/campaign-responses-csv', { params, responseType: 'blob' });
  },
  downloadCampaignResponsesExcel: (input: {
    organisationId?: string;
    campaignId: string;
    hierarchyScope?: 'entire_tree' | 'direct_only';
    from?: string;
    to?: string;
    state?: string;
    department?: string;
    q?: string;
  }) => {
    const params: Record<string, string> = {
      campaignId: input.campaignId,
    };
    if (input.organisationId) params.organisationId = input.organisationId;
    if (input.hierarchyScope) params.hierarchyScope = input.hierarchyScope;
    if (input.from) params.from = input.from;
    if (input.to) params.to = input.to;
    if (input.state && input.state !== 'all') params.state = input.state;
    if (input.department && input.department !== 'all') params.department = input.department;
    if (input.q?.trim()) params.q = input.q.trim();
    return api.get('/assessments/campaign-responses-excel', { params, responseType: 'blob' });
  },
  getPlatformStats: (from?: string, to?: string, modelName?: string, industry?: string) =>
    api.get('/assessments/platform-stats', {
      params: buildPlatformAnalyticsParams({ from, to, modelName, industry }),
    }),
  getPlatformAssessmentAnalytics: (from?: string, to?: string, modelName?: string, industry?: string) =>
    api.get('/assessments/platform-assessment-analytics', {
      params: buildPlatformAnalyticsParams({ from, to, modelName, industry }),
    }),
  getPlatformOrganisationSummaries: (from?: string, to?: string, modelName?: string, industry?: string) =>
    api.get('/assessments/platform-organisation-summaries', {
      params: buildPlatformAnalyticsParams({ from, to, modelName, industry }),
    }),
};

export const userAPI = {
  getAll: (params?: {
    role?: string;
    search?: string;
    isActive?: boolean;
    organisationId?: string;
    page?: number;
    pageSize?: number;
  }) =>
    api.get('/users', {
      params: {
        ...(params ?? {}),
        isActive: typeof params?.isActive === 'boolean' ? String(params.isActive) : undefined,
      },
    }),
  getById: (id: string | number) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string | number, data: any) => api.put(`/users/${id}`, data),
  setStatus: (id: string | number, isActive: boolean) => api.put(`/users/${id}/status`, { isActive }),
  remove: (id: string | number) => api.delete(`/users/${id}`),
};

export type TeamReportParams = {
  hierarchyScope?: 'entire_tree' | 'direct_only';
  managerUserId?: string;
};

function teamReportQueryParams(campaignId: string, params?: TeamReportParams) {
  const q: Record<string, string> = { campaignId };
  if (params?.hierarchyScope) q.hierarchyScope = params.hierarchyScope;
  if (params?.managerUserId) q.managerUserId = params.managerUserId;
  return q;
}

export const reportAPI = {
  previewTeamEngagementSummary: (campaignId: string, params?: TeamReportParams) =>
    api.get('/reports/team-engagement-summary/preview', {
      params: { ...teamReportQueryParams(campaignId, params), _: String(Date.now()) },
    }),
  previewDepartmentBreakdown: (campaignId: string, hierarchyScope?: 'entire_tree' | 'direct_only') =>
    api.get('/reports/department-breakdown/preview', { params: { campaignId, hierarchyScope } }),
  previewCampaignCompletion: (campaignId: string, hierarchyScope?: 'entire_tree' | 'direct_only') =>
    api.get('/reports/campaign-completion/preview', { params: { campaignId, hierarchyScope } }),
  downloadTeamEngagementSummary: (campaignId: string, params?: TeamReportParams) =>
    api.get('/reports/team-engagement-summary', {
      params: teamReportQueryParams(campaignId, params),
      responseType: 'blob',
    }),
  downloadDepartmentBreakdown: (campaignId: string, hierarchyScope?: 'entire_tree' | 'direct_only') =>
    api.get('/reports/department-breakdown', { params: { campaignId, hierarchyScope }, responseType: 'blob' }),
  downloadCampaignCompletion: (campaignId: string, hierarchyScope?: 'entire_tree' | 'direct_only') =>
    api.get('/reports/campaign-completion', { params: { campaignId, hierarchyScope }, responseType: 'blob' }),
  downloadCampaignCompletionCsv: (campaignId: string, hierarchyScope?: 'entire_tree' | 'direct_only') =>
    api.get('/reports/campaign-completion', { params: { campaignId, format: 'csv', hierarchyScope }, responseType: 'blob' }),
};

export const gdprAPI = {
  exportUser: (userId: string) => api.get(`/gdpr/export/${userId}`),
  anonymizeUser: (userId: string) => api.post('/gdpr/anonymize', { userId }),
  pruneAudit: (retentionDays: number) => api.post('/gdpr/prune-audit', { retentionDays }),
};

export default api;
